/**
 * The values the SERVER hands the page, per request.
 *
 * `go/internal/core/spa` templates a `window.__RUNTIME_CONFIG__` object into the
 * HTML before any module runs. It carries the three settings that are read at
 * request time on purpose — `analytics.tsx` states the rule: "switching hosts is
 * a container recreate, not a rebuild — do not inline them at build time" — plus
 * the CSP nonce, which client-rendered script tags need.
 *
 * # WHY THIS NEEDS NO VITE TWIN, WHEN router.ts AND config.ts DID
 *
 * Those two had to exist twice because their VALUES come from different places
 * per bundler: `next/navigation` versus `react-router-dom`, and
 * `process.env.NEXT_PUBLIC_*` versus `import.meta.env.VITE_*`. Neither has a
 * shape that works in both.
 *
 * This one reads a global that either exists or does not, so a single file
 * covers both: under Vite the server defines it and the `process.env` fallbacks
 * at the call sites are already `define`d to `undefined`; under Next the global
 * is absent and those fallbacks carry the value. Nothing to alias.
 */

export interface RuntimeConfig {
  /** Analytics tracker id. Empty or absent means analytics is off. */
  analyticsTrackerId?: string;
  /** `local`, `production`, … — analytics renders nothing when this is `local`. */
  appEnv?: string;
  /** Error-tracker app key for THIS app (admin and liff have different ones). */
  errorTrackerKey?: string;
  /**
   * This vertical's customer-facing origin, e.g. `https://clinic.example.com`.
   *
   * Composed server-side from SUBDOMAIN + DOMAIN, because admin and liff are
   * different hosts and the admin bundle has no other way to name its sibling.
   * Empty or absent when there is no real host to link to (see `liffOrigin` in
   * `go/internal/core/spa/fromenv.go`), and AdminLayout renders no link then.
   */
  liffOrigin?: string;
  /**
   * The mirror of liffOrigin: this vertical's dashboard origin, templated into
   * the LIFF pages. A signed-in admin gets an edit link on blog posts through
   * it; empty or absent means the link is not rendered.
   */
  adminOrigin?: string;
  /**
   * This request's CSP nonce.
   *
   * Needed because `'strict-dynamic'` makes browsers ignore `script-src 'self'`
   * entirely, so a script tag rendered by the client is blocked without it.
   */
  nonce?: string;
  /**
   * This vertical's colour theme id, already resolved by the server: its
   * `THEME` env value, or its company type's default when that is unset. Empty
   * or absent means the stock palette — see `findTheme` in `themes.ts`.
   */
  theme?: string;
  /**
   * This company's display name, looked up per request like `theme`. Admin shows
   * it in the topbar, the drawer and on the sign-in card, and every surface
   * prefixes the tab title with it — so staff with two verticals open can tell
   * the tabs apart. Absent means the generic titles.
   */
  companyName?: string;
  /**
   * This company's logo, the same uploads path the settings screen saves, read
   * per request beside `companyName`. Rendered before the name wherever the
   * name titles a surface — the admin topbar, the drawer, the sign-in card and
   * the customer home band. Absent or empty means the name stands alone.
   */
  logoUrl?: string;
  /**
   * The business's own website (the teisyovision marketing site), from
   * `WEBSITE_URL`, no trailing slash. When set, the liff header IS that site's
   * header -- its links, its bar, its button (see `liff/components/SiteHeader`)
   * -- so going from the site to booking and back reads as one site. Absent
   * keeps the booking app's own header.
   */
  websiteUrl?: string;
  /**
   * The clinic and gym booking apps' origins (CLINIC_BOOKING_URL /
   * GYM_BOOKING_URL, the website's own names). That header ends in a login
   * button for each, and its ブログ is the clinic app's /blog. Absent means
   * that app gets no button.
   */
  clinicBookingUrl?: string;
  gymBookingUrl?: string;
  /**
   * Which of the website's links that header carries, comma-separated (home,
   * clinic, gym, blog, company, contact): the site picks them per branch, and
   * 光明池's has no blog. Absent means all of them.
   */
  websiteNav?: string;
  /** The company type (CLINIC, GYM, ...): which website section is "here". */
  companyType?: string;
  /*
    REQUEST-TIME FIRST (teisyovision plan.md phase 10). The three values the booking repo
    bakes in at build time, given at request time instead: one build of each
    admin app serves both branches (玉出 and 光明池), and the site server knows
    which vertical a host is. Read first by config.ts, which falls back to the
    build-time VITE_ values.
  */
  /** The auth service's public origin. */
  authServiceUrl?: string;
  /** This vertical's OAuth client id (clinic, gym-komyoike, ...). */
  oauthClientId?: string;
  /** This vertical's company id in its own database. */
  companyId?: number;
  /**
   * The product lines this dashboard serves, comma-separated: `clinic`, `gym`,
   * or both where one company runs the two from one database (teisyovision
   * plan.md phase 13). Set by the site server per host, so one admin build
   * serves a branch before its merge (two hosts, one line each) and after it
   * (one host, both). Absent means the app's own default.
   */
  productLines?: string;
}

declare global {
  var __RUNTIME_CONFIG__: RuntimeConfig | undefined;
}

/**
 * Read once at module scope. The server writes it before the app's own script
 * tag, so it is present by the time any module body runs, and it never changes
 * within a page load.
 */
export const runtimeConfig: RuntimeConfig =
  (typeof globalThis !== "undefined" && globalThis.__RUNTIME_CONFIG__) || {};

/*
  The tab title, prefixed with the company. Both index.html titles are generic
  ("管理", "予約システム") and the same in every vertical, which is exactly what
  made two open admins indistinguishable in a tab strip. Done here, once, because
  this module is the first to know the name and every surface imports it.
*/
if (runtimeConfig.companyName && typeof document !== "undefined") {
  document.title = `${runtimeConfig.companyName}｜${document.title}`;
}
